import React, { useState, useEffect, useCallback } from 'react';
import { imageApi } from '../../../api/imageApi.js';
import { albumApi } from '../../../api/albumApi.js';
import { useParams, useNavigate } from 'react-router-dom';
import { useImageGestures } from './useImageGestures.js';
import './imageModal.css';
import './zoomableImage.css';

const ImageModal = ({ imageList }) => {
    const { albumID, imageID } = useParams();
    const [imageSrc, setImageSrc] = useState("");
    const [imageDate, setImageDate] = useState("");
    const [description, setDescription] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const [imageUrl, details] = await Promise.all([
                    imageApi.getImageBytes(imageID),
                    imageApi.getImageDetails(imageID),
                ]);

                setImageSrc(imageUrl);
                setImageDate(details.data.data.takenOn);
                setDescription(details.data.data.description);
            } catch (error) {
                console.error("Error fetching the image and details:", error);
            }
        })();
    }, [imageID]);

    const navigateToNextImage = useCallback(async () => {
        let response = await albumApi.getNextPhotoInAlbum(albumID, imageID);
        let photoID = response.data.nextPhotoID;

        if (photoID > 0) {
            navigate(`/albums/${albumID}/` + photoID);
        }
    }, [albumID, imageID, navigate]);

    const navigateToPrevImage = useCallback(async () => {
        let response = await albumApi.getPreviousPhotoInAlbum(albumID, imageID);
        let photoID = response.data.previousPhotoID;

        if (photoID > 0) {
            navigate(`/albums/${albumID}/` + photoID);
        }
    }, [albumID, imageID, navigate]);

    const { containerRef, transform, resetZoom, isZoomed } = useImageGestures({
        resetKey: imageID,
        onSwipeLeft: navigateToNextImage,
        onSwipeRight: navigateToPrevImage,
    });

    useEffect(() => {
        sessionStorage.setItem(`album-last-${albumID}`, String(imageID));
    }, [albumID, imageID]);

    const closeAndNavigate = () => {
        navigate(`/albums/${albumID}`);
    };

    return (
        <div
            className={`fullscreen-image-container image-gesture-container${isZoomed ? ' is-zoomed' : ''}`}
            ref={containerRef}
        >
            <div className="image-zoom-viewport">
                <img
                    src={imageSrc}
                    alt="Detail"
                    draggable={false}
                    style={{
                        transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
                    }}
                    onLoad={resetZoom}
                />
            </div>
            {isZoomed && (
                <button
                    type="button"
                    className="reset-zoom-button"
                    onClick={resetZoom}
                    title="Reset zoom"
                >
                    Reset zoom
                </button>
            )}
            <div className="image-date">{imageDate}</div>
            <div className="image-description">{description}</div>
            <button className="close-button" onClick={closeAndNavigate}>X</button>
            <div className="navigation">
                <span className="icon arrow-left" onClick={navigateToPrevImage}>◀️</span>
                <span className="icon arrow-right" onClick={navigateToNextImage}>▶️</span>
            </div>
        </div>
    );
}

export { ImageModal };
